import { useMemo } from 'react'

import { resourceListTag, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  ImportProfilesScreen,
  ONEC_IMPORT_TAG,
  attemptKey,
  attemptRecorded,
  buildCreateProfileArgs,
  buildDeleteProfileArgs,
  buildUpdateProfileArgs,
  emptyProfileDraft,
  importApi,
  makeSelectProfilesVm,
  newCommandAttempt,
  onecImportCreateProfile,
  onecImportDeleteProfile,
  onecImportProfiles,
  onecImportUpdateProfile,
  onecConnectionForm,
  profileColumnAdded,
  profileColumnChanged,
  profileColumnRemoved,
  profileEditorChanged,
  profileEditorClosed,
  profileEditorOpened,
  profileFilterChanged,
  profileToDraft,
  DEFAULT_PROFILE_FILTER,
  type ProfilesActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useResourceRevision } from '../platform/useResourceRevision'

/** Binding окна «Профили колонок импорта»: список, создание, правка и удаление в пределах rid окна. */
export function ImportProfilesBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const intent = useAppSelector((state) => state.onec.importFlow)
  const filter = intent.profileFilters[resourceId] ?? DEFAULT_PROFILE_FILTER

  const args = useMemo(
    () =>
      resourceId === ''
        ? null
        : {
            resourceId,
            includeInactive: filter.includeInactive,
            supplier: filter.supplier.trim() === '' ? null : filter.supplier.trim(),
          },
    [resourceId, filter.includeInactive, filter.supplier],
  )
  const query = useQueryResult(onecImportProfiles, args)
  const [createTrigger, create] = onecImportCreateProfile.useMutation({ fixedCacheKey: `${resourceId}:profile-create` })
  const [updateTrigger, update] = onecImportUpdateProfile.useMutation({
    fixedCacheKey: `${resourceId}:profile-update:${intent.profileEditors[resourceId]?.id ?? ''}`,
  })
  const [removeTrigger, remove] = onecImportDeleteProfile.useMutation({ fixedCacheKey: `${resourceId}:profile-delete` })

  const resourceVersion = useResourceRevision(resourceId, onecConnectionForm.kind, 'import.read')
  const selectVm = useMemo(() => makeSelectProfilesVm(), [])
  const vm = selectVm({ resourceId, intent, query, create, update, remove, resourceVersion })

  const actions = useMemo<ProfilesActions>(
    () => ({
      reload: () => dispatch(importApi.util.invalidateTags([resourceListTag(ONEC_IMPORT_TAG, resourceId)])),
      filterChanged: (patch) => dispatch(profileFilterChanged({ resourceId, patch })),
      createStarted: () => dispatch(profileEditorOpened({ resourceId, draft: emptyProfileDraft() })),
      editStarted: (profileId) => {
        const profile = query.data?.data.find((candidate) => candidate.id === profileId)
        if (profile !== undefined) dispatch(profileEditorOpened({ resourceId, draft: profileToDraft(profile) }))
      },
      editorChanged: (patch) => dispatch(profileEditorChanged({ resourceId, patch })),
      editorColumnChanged: (index, patch) => dispatch(profileColumnChanged({ resourceId, index, patch })),
      editorColumnAdded: () => dispatch(profileColumnAdded({ resourceId })),
      editorColumnRemoved: (index) => dispatch(profileColumnRemoved({ resourceId, index })),
      editorClosed: () => dispatch(profileEditorClosed({ resourceId })),
      editorSaved: () => {
        const draft = vm.editor?.draft
        if (draft === undefined || vm.configurationVersion === null || vm.editor?.canSave !== true) return
        const key = attemptKey('profile', resourceId, draft.id ?? 'new')
        const attempt = intent.attempts[key] ?? newCommandAttempt()
        dispatch(attemptRecorded({ key, attempt }))
        void (draft.id === null
          ? createTrigger(buildCreateProfileArgs(resourceId, vm.configurationVersion, draft, attempt))
          : updateTrigger(buildUpdateProfileArgs(resourceId, vm.configurationVersion, draft.id, draft, attempt)))
      },
      deleted: (profileId) => {
        if (vm.configurationVersion === null) return
        const key = attemptKey('delete', resourceId, profileId)
        const attempt = intent.attempts[key] ?? newCommandAttempt()
        dispatch(attemptRecorded({ key, attempt }))
        void removeTrigger(buildDeleteProfileArgs(resourceId, vm.configurationVersion, profileId, attempt))
      },
    }),
    [dispatch, resourceId, query.data, vm, intent.attempts, createTrigger, updateTrigger, removeTrigger],
  )

  return <ImportProfilesScreen vm={vm} actions={actions} />
}
